import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Avatar,
  Button,
  Card,
  Col,
  DatePicker,
  Empty,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import type { FilterValue, SorterResult } from 'antd/es/table/interface';
import {
  DownloadOutlined,
  GoogleOutlined,
  ReloadOutlined,
  SearchOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import dayjs, { type Dayjs } from 'dayjs';

import { extractApiErrorMessage } from '@/api/client';
import { listAssignments } from '@/api/assignments';
import { listCommunityCourses } from '@/api/courses';
import { exportSubmissionsXlsx, listSubmissions } from '@/api/submissions';
import { isHiddenCommunity, listCommunities } from '@/api/students';
import {
  ASSIGNMENT_TYPE_LABELS,
  SUBMISSION_STATUS_LABELS,
  type AssignmentType,
  type SubmissionStatus,
} from '@/types/assignments';
import type { SubmissionListParams, SubmissionRow } from '@/types/submissions';
import { SubmissionDetailDrawer } from './AssignmentDetailDrawer';
import { GoogleSheetsSyncModal } from './GoogleSheetsSyncModal';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const DEFAULT_PAGE_SIZE = 20;
const PAGE_SIZE_OPTIONS = ['10', '20', '50', '100'];
const SEARCH_DEBOUNCE_MS = 350;
const TYPE_COLOR: Record<AssignmentType, string> = {
  NORMAL: 'blue',
  WEEK: 'cyan',
  EXAM: 'volcano',
};

const STATUS_COLOR: Record<SubmissionStatus, string> = {
  SUBMITTED: 'gold',
  REVIEWED: 'blue',
  NEEDS_REVISION: 'orange',
  APPROVED: 'green',
  SUBMIT_LATER: 'red',
};

const STATUS_OPTIONS = (
  Object.keys(SUBMISSION_STATUS_LABELS) as SubmissionStatus[]
).map((value) => ({ value, label: SUBMISSION_STATUS_LABELS[value] }));

interface ParsedFilters {
  communityId?: number;
  courseId?: number;
  assignmentIds: number[];
  cohorts: number[];
  q?: string;
  dueDateFrom?: string;
  dueDateTo?: string;
  status?: SubmissionStatus;
  page: number;
  size: number;
  sort: string;
}

function parseCsvNumbers(raw: string | null): number[] {
  if (!raw) return [];
  return raw
    .split(',')
    .map((s) => Number(s.trim()))
    .filter((n) => Number.isFinite(n) && n > 0);
}

function parseSearchParams(params: URLSearchParams): ParsedFilters {
  const pageRaw = Number(params.get('page') ?? '0');
  const sizeRaw = Number(params.get('size') ?? String(DEFAULT_PAGE_SIZE));
  const communityIdRaw = params.get('communityId');
  const courseIdRaw = params.get('courseId');
  const statusRaw = params.get('status') as SubmissionStatus | null;
  return {
    communityId:
      communityIdRaw && Number(communityIdRaw) > 0 ? Number(communityIdRaw) : undefined,
    courseId:
      courseIdRaw && Number(courseIdRaw) > 0 ? Number(courseIdRaw) : undefined,
    assignmentIds: parseCsvNumbers(params.get('assignmentIds')),
    cohorts: parseCsvNumbers(params.get('cohorts')),
    q: params.get('q') ?? undefined,
    dueDateFrom: params.get('dueDateFrom') ?? undefined,
    dueDateTo: params.get('dueDateTo') ?? undefined,
    status:
      statusRaw && statusRaw in SUBMISSION_STATUS_LABELS ? statusRaw : undefined,
    page: Number.isFinite(pageRaw) && pageRaw >= 0 ? pageRaw : 0,
    size: Number.isFinite(sizeRaw) && sizeRaw > 0 ? sizeRaw : DEFAULT_PAGE_SIZE,
    sort: params.get('sort') ?? 'submittedAt,desc',
  };
}

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  const d = dayjs(iso);
  return d.isValid() ? d.format('DD/MM/YYYY HH:mm') : '—';
}

export function AssignmentListPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => parseSearchParams(searchParams), [searchParams]);

  const [selectedSubmissionId, setSelectedSubmissionId] = useState<number | null>(
    null,
  );

  // Debounce student-search input so we don't refetch on every keystroke.
  const [searchInput, setSearchInput] = useState<string>(filters.q ?? '');
  useEffect(() => {
    setSearchInput(filters.q ?? '');
  }, [filters.q]);
  useEffect(() => {
    const trimmedInput = searchInput.trim();
    const current = filters.q ?? '';
    if (trimmedInput === current) return;
    const handle = window.setTimeout(() => {
      updateParam({ q: trimmedInput === '' ? undefined : trimmedInput });
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const queryParams: SubmissionListParams = {
    communityId: filters.communityId,
    courseId: filters.courseId,
    assignmentIds:
      filters.assignmentIds.length > 0 ? filters.assignmentIds : undefined,
    cohorts: filters.cohorts.length > 0 ? filters.cohorts : undefined,
    q: filters.q,
    dueDateFrom: filters.dueDateFrom,
    dueDateTo: filters.dueDateTo,
    status: filters.status,
    page: filters.page,
    size: filters.size,
    sort: filters.sort,
  };

  const [exporting, setExporting] = useState(false);
  const [syncSheetsOpen, setSyncSheetsOpen] = useState(false);

  const handleExport = async () => {
    setExporting(true);
    try {
      const blob = await exportSubmissionsXlsx({
        ...queryParams,
        // page/size are ignored server-side; remove to avoid noise
        page: undefined,
        size: undefined,
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const stamp = dayjs().format('YYYYMMDD-HHmm');
      link.download = `bai-lam-hoc-vien-${stamp}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      message.success('Đã tải file Excel');
    } catch (err) {
      message.error(
        extractApiErrorMessage(err, 'Không xuất được file Excel.'),
      );
    } finally {
      setExporting(false);
    }
  };

  const query = useQuery({
    queryKey: ['submissions', queryParams],
    queryFn: () => listSubmissions(queryParams),
    placeholderData: keepPreviousData,
  });

  const communitiesQuery = useQuery({
    queryKey: ['admin-communities'],
    queryFn: listCommunities,
    staleTime: 5 * 60_000,
  });

  const coursesQuery = useQuery({
    queryKey: ['assignments-courses', filters.communityId],
    queryFn: () => listCommunityCourses(filters.communityId as number),
    enabled: filters.communityId !== undefined,
    staleTime: 60_000,
  });

  const assignmentsQuery = useQuery({
    // When a course is picked the dropdown narrows to that course's assignments
    // — keeps the assignmentIds multi-select scoped to a relevant short list.
    queryKey: ['assignments-options', filters.communityId, filters.courseId],
    queryFn: () =>
      listAssignments({
        communityId: filters.communityId,
        courseId: filters.courseId,
        page: 0,
        size: 200,
        sort: 'createdAt,desc',
      }),
    staleTime: 60_000,
  });

  const assignmentOptions = (assignmentsQuery.data?.content ?? []).map((a) => ({
    value: a.id,
    label: a.title ?? `Bài tập #${a.id}`,
  }));

  // When the admin has narrowed to exactly one assignment with a dueDate,
  // suggest that date (YYYYMMDD) as the Google Sheets tab name so each
  // due-date gets its own tab automatically (vd "20260520"). Multi-assignment
  // or no-assignment falls back to localStorage / default inside the modal.
  const suggestedSheetName = useMemo<string | null>(() => {
    if (filters.assignmentIds.length !== 1) return null;
    const targetId = filters.assignmentIds[0];
    const ass = assignmentsQuery.data?.content.find((a) => a.id === targetId);
    if (!ass?.dueDate) return null;
    return dayjs(ass.dueDate).format('YYYYMMDD');
  }, [filters.assignmentIds, assignmentsQuery.data]);

  const communityName = useMemo(() => {
    if (!filters.communityId) return null;
    const found = (communitiesQuery.data ?? []).find(
      (c) => c.id === filters.communityId,
    );
    return found?.name ?? null;
  }, [communitiesQuery.data, filters.communityId]);

  const updateParam = (
    patch: Partial<Record<string, string | undefined>>,
    resetPage = true,
  ) => {
    const next = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(patch)) {
      if (value === undefined || value === '' || value === null) {
        next.delete(key);
      } else {
        next.set(key, value);
      }
    }
    if (resetPage) next.delete('page');
    setSearchParams(next, { replace: true });
  };

  const handleTableChange = (
    pagination: TablePaginationConfig,
    _filters: Record<string, FilterValue | null>,
    sorter: SorterResult<SubmissionRow> | SorterResult<SubmissionRow>[],
  ) => {
    const next = new URLSearchParams(searchParams);
    const current = pagination.current ?? 1;
    const pageSize = pagination.pageSize ?? DEFAULT_PAGE_SIZE;
    next.set('page', String(Math.max(0, current - 1)));
    next.set('size', String(pageSize));

    const singleSorter = Array.isArray(sorter) ? sorter[0] : sorter;
    if (singleSorter?.field && singleSorter.order) {
      const direction = singleSorter.order === 'ascend' ? 'asc' : 'desc';
      next.set('sort', `${String(singleSorter.field)},${direction}`);
    }

    setSearchParams(next, { replace: true });
  };

  const handleAssignmentChange = (values: number[]) => {
    updateParam({
      assignmentIds: values.length === 0 ? undefined : values.join(','),
    });
  };

  // Switching course invalidates the currently-picked assignmentIds (they may
  // belong to a different course). Clear them in the same URL update so the
  // submissions query refetches with a coherent filter set.
  const handleCourseChange = (value: number | null | undefined) => {
    updateParam({
      courseId: value === null || value === undefined ? undefined : String(value),
      assignmentIds: undefined,
    });
  };

  const courseOptions = (coursesQuery.data ?? []).map((c) => ({
    value: c.id,
    label: c.name ?? `Khoá #${c.id}`,
  }));

  // Backend still accepts a list (`cohorts=K1&cohorts=K2`); we just constrain the
  // UI to one value at a time so the user can type the cohort number instead of
  // hunting through a dropdown. Existing share-links with multiple cohorts keep
  // working — only the first value will show in the input.
  const handleCohortChange = (value: number | null) => {
    updateParam({
      cohorts: value === null || value === undefined ? undefined : String(value),
    });
  };

  const handleDueDateChange = (range: [Dayjs | null, Dayjs | null] | null) => {
    if (!range || (!range[0] && !range[1])) {
      updateParam({ dueDateFrom: undefined, dueDateTo: undefined });
      return;
    }
    const [from, to] = range;
    updateParam({
      dueDateFrom: from ? from.startOf('day').toISOString() : undefined,
      dueDateTo: to ? to.endOf('day').toISOString() : undefined,
    });
  };

  const dueDateValue: [Dayjs | null, Dayjs | null] | null =
    filters.dueDateFrom || filters.dueDateTo
      ? [
          filters.dueDateFrom ? dayjs(filters.dueDateFrom) : null,
          filters.dueDateTo ? dayjs(filters.dueDateTo) : null,
        ]
      : null;

  const columns: ColumnsType<SubmissionRow> = [
    {
      title: 'Học viên',
      key: 'user',
      width: 260,
      render: (_, row) => (
        <Space>
          <Avatar
            src={row.avatarUrl ?? undefined}
            icon={<UserOutlined />}
            size={36}
          />
          <Space direction="vertical" size={0}>
            <Text strong>{row.fullname ?? '—'}</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {row.email ?? row.mobile ?? `User #${row.userId}`}
            </Text>
          </Space>
        </Space>
      ),
    },
    {
      title: 'Bài tập',
      key: 'assignment',
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          <Text>{row.assignmentTitle ?? `#${row.assignmentId}`}</Text>
          {row.assignmentType ? (
            <Tag color={TYPE_COLOR[row.assignmentType]} style={{ marginInlineEnd: 0 }}>
              {ASSIGNMENT_TYPE_LABELS[row.assignmentType]}
            </Tag>
          ) : null}
        </Space>
      ),
    },
    {
      title: 'Cộng đồng',
      key: 'community',
      width: 180,
      // Hide on mobile/tablet — community context is already in the URL filter
      // bar; admin doesn't need to scan this column on small screens.
      responsive: ['lg'],
      render: (_, row) =>
        isHiddenCommunity(row.communityId) || !row.communityName ? (
          '—'
        ) : (
          <Text>{row.communityName}</Text>
        ),
    },
    {
      title: 'Hạn nộp',
      key: 'dueDate',
      dataIndex: 'assignmentDueDate',
      width: 160,
      sorter: true,
      responsive: ['md'],
      render: (value: string | null) => formatDateTime(value),
    },
    {
      title: 'Trạng thái',
      key: 'status',
      dataIndex: 'status',
      width: 130,
      sorter: true,
      render: (value: SubmissionStatus | null) =>
        value ? (
          <Tag color={STATUS_COLOR[value]}>{SUBMISSION_STATUS_LABELS[value]}</Tag>
        ) : (
          '—'
        ),
    },
    {
      title: 'Nộp lúc',
      key: 'submittedAt',
      dataIndex: 'submittedAt',
      width: 160,
      sorter: true,
      responsive: ['md'],
      render: formatDateTime,
    },
  ];

  const sortField = filters.sort.split(',')[0];
  const sortOrder = filters.sort.split(',')[1];

  const columnsWithSort = columns.map((col) => {
    if (!col.sorter) return col;
    const field =
      'dataIndex' in col && col.dataIndex
        ? String(col.dataIndex)
        : 'key' in col && col.key
          ? String(col.key)
          : '';
    if (field === sortField) {
      return {
        ...col,
        sortOrder: (sortOrder === 'asc' ? 'ascend' : 'descend') as 'ascend' | 'descend',
      };
    }
    return col;
  });

  const pageData = query.data;
  const errorMessage = query.error
    ? extractApiErrorMessage(query.error, 'Không tải được danh sách bài làm.')
    : null;

  return (
    <div className="submission-list">
      <div className="student-list-header">
        <div>
          <Title level={3} style={{ margin: 0 }}>
            Bài làm học viên
          </Title>
          <Text type="secondary">
            {pageData
              ? `${pageData.totalElements.toLocaleString('vi-VN')} bài làm${
                  filters.communityId
                    ? ` · ${communityName ?? `community #${filters.communityId}`}`
                    : ' · tất cả cộng đồng'
                }`
              : 'Đang tải…'}
          </Text>
        </div>
        <Space>
          <Button
            icon={<DownloadOutlined />}
            type="primary"
            onClick={handleExport}
            loading={exporting}
          >
            <span className="btn-label-hide-xs">Xuất Excel</span>
          </Button>
          <Button
            icon={<GoogleOutlined />}
            onClick={() => setSyncSheetsOpen(true)}
          >
            <span className="btn-label-hide-xs">Đồng bộ Google Sheets</span>
          </Button>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => query.refetch()}
            loading={query.isFetching}
          >
            Tải lại
          </Button>
        </Space>
      </div>

      <Card variant="borderless" className="filter-card">
        {/* Responsive filter grid: 1 col mobile / 2 col sm / 3 col md / 4 col lg.
            Each control fills its column (width 100%) — drop AntD's fixed px
            widths so nothing overflows the viewport at narrow widths. */}
        <Row gutter={[12, 12]}>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Select<number>
              allowClear
              showSearch
              placeholder="Khoá học"
              options={courseOptions}
              value={filters.courseId ?? undefined}
              onChange={handleCourseChange}
              loading={coursesQuery.isLoading}
              optionFilterProp="label"
              disabled={filters.communityId === undefined}
              style={{ width: '100%' }}
            />
          </Col>

          <Col xs={24} sm={12} md={8} lg={6}>
            <Select<number[]>
              mode="multiple"
              allowClear
              showSearch
              placeholder="Tất cả bài tập"
              options={assignmentOptions}
              value={filters.assignmentIds}
              onChange={handleAssignmentChange}
              loading={assignmentsQuery.isLoading}
              optionFilterProp="label"
              maxTagCount="responsive"
              style={{ width: '100%' }}
            />
          </Col>

          <Col xs={24} sm={12} md={8} lg={6}>
            <RangePicker
              placeholder={['Hạn nộp từ', 'đến']}
              value={dueDateValue}
              onChange={handleDueDateChange}
              format="DD/MM/YYYY"
              style={{ width: '100%' }}
            />
          </Col>

          <Col xs={12} sm={6} md={4} lg={3}>
            <InputNumber
              min={1}
              placeholder="Khoá"
              value={filters.cohorts[0] ?? undefined}
              onChange={handleCohortChange}
              style={{ width: '100%' }}
            />
          </Col>

          <Col xs={24} sm={12} md={8} lg={6}>
            <Input
              allowClear
              prefix={<SearchOutlined />}
              placeholder="Tìm học viên (tên, email, SĐT)"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              style={{ width: '100%' }}
            />
          </Col>

          <Col xs={12} sm={6} md={4} lg={3}>
            <Select
              allowClear
              placeholder="Trạng thái"
              options={STATUS_OPTIONS}
              value={filters.status}
              onChange={(value) => updateParam({ status: value })}
              style={{ width: '100%' }}
            />
          </Col>
        </Row>
      </Card>

      {errorMessage ? (
        <Alert
          type="error"
          showIcon
          message="Lỗi"
          description={errorMessage}
          style={{ marginTop: 16 }}
        />
      ) : null}

      <Card variant="borderless" className="table-card">
        <Table<SubmissionRow>
          rowKey="id"
          columns={columnsWithSort}
          dataSource={pageData?.content ?? []}
          loading={query.isFetching}
          // Keep wide tables horizontally scrollable instead of squishing
          // columns at narrow widths. 'max-content' = fit columns naturally.
          scroll={{ x: 'max-content' }}
          locale={{ emptyText: <Empty description="Không có bài làm nào" /> }}
          pagination={{
            current: filters.page + 1,
            pageSize: filters.size,
            total: pageData?.totalElements ?? 0,
            showSizeChanger: true,
            pageSizeOptions: PAGE_SIZE_OPTIONS,
            showTotal: (total, range) =>
              `${range[0]}–${range[1]} / ${total.toLocaleString('vi-VN')}`,
          }}
          onChange={handleTableChange}
          onRow={(row) => ({
            onClick: () => setSelectedSubmissionId(row.id),
            style: { cursor: 'pointer' },
          })}
        />
      </Card>

      <SubmissionDetailDrawer
        submissionId={selectedSubmissionId}
        open={selectedSubmissionId !== null}
        onClose={() => setSelectedSubmissionId(null)}
      />

      <GoogleSheetsSyncModal
        open={syncSheetsOpen}
        onClose={() => setSyncSheetsOpen(false)}
        filterParams={{ ...queryParams, page: undefined, size: undefined }}
        suggestedSheetName={suggestedSheetName}
        communityAssignmentResultSheetUrl={
          communitiesQuery.data?.find(
            (c) => c.id === filters.communityId,
          )?.assignmentResultSheetUrl ?? null
        }
        communityAssignmentResultSheetName={
          communitiesQuery.data?.find(
            (c) => c.id === filters.communityId,
          )?.assignmentResultSheetName ?? null
        }
      />
    </div>
  );
}
